import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
  inject,
  output,
  effect,
  } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SchemaMembershipStateService } from '../../../../core';
import { IconComponent } from '../../../../shared/components/ui/icon/icon.component';
import { ScheduleCalendarDayEdit } from '../calendar-day-edit/schedule-calendar-day-edit';
import { FormControl, FormRecord, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'lib-schedule-calendar-edit',
  imports: [CommonModule, IconComponent, ScheduleCalendarDayEdit, ReactiveFormsModule],
  templateUrl: './schedule-calendar-edit.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleCalendarEdit {
  private schemaState = inject(SchemaMembershipStateService);

  // mirem si és volum des de props del schema
  readonly isByVolume = computed(() => {
    const props = this.schemaState.properties() as any;
    const byVolume = props?.irrigationScheduleInVolume ?? false;
    return byVolume;
  });
  
  formDayRecord = input.required<FormRecord>();
  loading = input.required<boolean>();
  monthChangeRequested = output<Date>();
  viewMonth = input.required<Date>();

  today = new Date((new Date()).setHours(0, 0, 0, 0));
  
  monthDates: string[];
  
  previousMonthEmptySpaces = computed(() => {
    const date = this.viewDate();
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);

    //! setmana comença en dilluns (1)
    let startDay = firstDayOfMonth.getDay();
    startDay = startDay === 0 ? 6 : startDay - 1;
    
    const days: string[] = [];
   //@ dies mes anterior per omplir inici
    const prevMonthLastDay = new Date(this.viewDate().getFullYear(), this.viewDate().getMonth(), 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonthLastDay - i);
      days.push(d.toLocaleDateString('en-CA').slice(0, 10)); // yyyy-mm-dd
    } 
    return days;
  })

  nextMonthEmptySpaces = computed(() => {
    const date = this.viewDate();
    const year = date.getFullYear();
    const month = date.getMonth();

    const lastDayOfMonth = new Date(year, month + 1, 0);

    //@ dies mes següent per tancar última fila
    let lastDay = lastDayOfMonth.getDay();
    lastDay = lastDay === 0 ? 6 : lastDay - 1; // ajustem a dilluns (0-6)

    const days: string[] = [];
    const remainingDays = 6 - lastDay;
    if (remainingDays > 0) {
      for (let i = 1; i <= remainingDays; i++) {
        const d = new Date(year, month + 1, i);
        days.push(d.toLocaleDateString('en-CA').slice(0, 10));
      }
    }
    return days;
  })

  constructor() {
    this.monthDates = this.getMonthDates(this.viewDate());
    effect(() => {
      this.monthDates = this.getMonthDates(this.viewDate());
    })
    effect(() => {
      const currentDate = this.viewMonth();
      this.viewDate.set(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
    });
  }
  
  
  controlFor(date: string): FormControl {
    return this.formDayRecord().controls[date] as FormControl;
  }
  
  // util/calendar.ts
  getMonthDates(date: Date): string[] {
    let year: number = date.getFullYear();
    let month: number = date.getMonth();
    const result: string[] = [];
    const d = new Date(year, month, 1);

    while (d.getMonth() === month) {
      result.push(d.toLocaleDateString('en-CA').slice(0, 10)); // yyyy-mm-dd
      d.setDate(d.getDate() + 1);
    }


    return result;
  }


  //! constants limit navegació
  private readonly minDate = new Date(
    this.today.getFullYear(),
    this.today.getMonth(),
    1
  );
  private readonly maxDate = new Date(
    this.today.getFullYear(),
    this.today.getMonth() + 3,
    1
  );

  viewDate = signal(
    new Date(this.today.getFullYear(), this.today.getMonth(), 1)
  );

  daysOfWeek = ['1', '2', '3', '4', '5', '6', '7'];
  monthNames = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  // nom del mes i any actuals de viewdate
  currentMonthName = computed(
    () => this.monthNames[this.viewDate().getMonth()]
  );
  currentYear = computed(() => this.viewDate().getFullYear());

  //? limits navegació per botons
  canGoPrev = computed(() => {
    const prev = new Date(
      this.viewDate().getFullYear(),
      this.viewDate().getMonth() - 1,
      1
    );
    return prev >= this.minDate;
  });

  canGoNext = computed(() => {
    const next = new Date(
      this.viewDate().getFullYear(),
      this.viewDate().getMonth() + 1,
      1
    );
    return next <= this.maxDate;
  });

  //@ accions per canviar de mes
  requestPrevMonth() {
    if (!this.canGoPrev()) return;
    const target = new Date(this.viewDate().getFullYear(), this.viewDate().getMonth() - 1, 1);
    this.monthChangeRequested.emit(target); // Emitim el canvi al pare per a que mostri el modal de descartar si cal
  }

  requestNextMonth() {
    if (!this.canGoNext()) return;
    const target = new Date(this.viewDate().getFullYear(), this.viewDate().getMonth() + 1, 1);
    this.monthChangeRequested.emit(target); // Emitim el canvi al pare per a que mostri el modal de descartar si cal
  }

  formatDate(date: string){
    return new Date(date);
  }

  isCurrentMonth(stringDate: string){
    let date = new Date(stringDate);
    return date.getMonth() === this.viewDate().getMonth();
  }

  dateString = computed( () => {
    const d = this.viewDate();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}-${yyyy}`; // MM-YYYY
  });
  

  getDateDay(date: string){
    return new Date(date).getDate()
  }

}
